import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ContentLayout from "@cloudscape-design/components/content-layout";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import Alert from "@cloudscape-design/components/alert";
import Spinner from "@cloudscape-design/components/spinner";
import Box from "@cloudscape-design/components/box";
import Checkbox from "@cloudscape-design/components/checkbox";
import ScenarioTile from "./ScenarioTile";
import ScenarioModal from "./ScenarioModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import CallModal from "./CallModal";
import { useAuth } from "../contexts/AuthContext";
import { listScenarios, createScenario, updateScenario, deleteScenario } from "../services/scenarioApi";
import "./ScenariosView.css";

const VISIBILITY_PUBLIC_KEY = "scenario-filter-vis-public";
const VISIBILITY_PRIVATE_KEY = "scenario-filter-vis-private";
const STATUS_PUBLISHED_KEY = "scenario-filter-status-published";
const STATUS_DRAFT_KEY = "scenario-filter-status-draft";

function getStoredCheck(key, defaultValue) {
  try {
    const stored = sessionStorage.getItem(key);
    if (stored === null) return defaultValue;
    return stored === "true";
  } catch {
    return defaultValue;
  }
}

function ScenariosView({ onNavigate }) {
  const { t } = useTranslation();
  const { user, isWriteUser } = useAuth();
  const currentUserSub = user?.sub || null;

  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showPublic, setShowPublic] = useState(() => getStoredCheck(VISIBILITY_PUBLIC_KEY, true));
  const [showPrivate, setShowPrivate] = useState(() => getStoredCheck(VISIBILITY_PRIVATE_KEY, true));
  const [showPublished, setShowPublished] = useState(() => getStoredCheck(STATUS_PUBLISHED_KEY, true));
  const [showDraft, setShowDraft] = useState(() => getStoredCheck(STATUS_DRAFT_KEY, true));

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedScenario, setSelectedScenario] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showCallModal, setShowCallModal] = useState(false);
  const [callDirection, setCallDirection] = useState("inbound");

  const fetchScenarios = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await listScenarios({ isActive: true });
      setScenarios(data || []);
    } catch (err) {
      setError(err.message || t("scenarios.errors.fetch_failed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const filteredScenarios = scenarios.filter((scenario) => {
    // Non-federated users see all scenarios without filtering
    if (!isWriteUser) return true;
    const visMatch = (showPublic && scenario.visibility === "public") || (showPrivate && scenario.visibility === "private");
    const statusMatch = (showPublished && scenario.status === "published") || (showDraft && scenario.status === "draft");
    return visMatch && statusMatch;
  });

  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  const handleCreateScenario = async (data) => {
    await createScenario(data);
    setShowModal(false);
    await fetchScenarios();
  };

  const handleEditScenario = async (data, scenarioId) => {
    await updateScenario(scenarioId, data);
    setShowModal(false);
    setSelectedScenario(null);
    await fetchScenarios();
  };

  const handleDeleteScenario = async () => {
    if (!selectedScenario) return;
    setDeleteLoading(true);

    try {
      await deleteScenario(selectedScenario.scenario_id);
      setShowDeleteModal(false);
      setSelectedScenario(null);
      await fetchScenarios();
    } catch (err) {
      if (err.statusCode === 403) {
        setError(t("errors.api.forbidden"));
        setShowDeleteModal(false);
        setSelectedScenario(null);
        await fetchScenarios();
      } else {
        setError(err.message || t("scenarios.errors.delete_failed"));
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleScenarioClick = (scenario) => {
    const direction = scenario.call_direction === "outbound" ? "outbound" : "inbound";
    setSelectedScenario(scenario);
    setCallDirection(direction);
    setShowCallModal(true);
  };

  const handleCallStart = (callResponse) => {
    setShowCallModal(false);
    onNavigate("conversation", { scenario: selectedScenario, callResponse });
  };

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("scenarios.view.description")}
          actions={
            isWriteUser && (
              <Button variant="primary" iconName="add-plus" onClick={() => { setModalMode("create"); setSelectedScenario(null); setShowModal(true); }}>
                {t("scenarios.view.add_button")}
              </Button>
            )
          }
        >
          {t("scenarios.view.title")}
        </Header>
      }
    >
      <Container>
        <SpaceBetween size="l">
          {error && (
            <Alert type="error" dismissible onDismiss={() => setError(null)}
              action={<Button onClick={fetchScenarios}>{t("errors.api.try_again")}</Button>}>
              {error}
            </Alert>
          )}

          {!isWriteUser && (
            <Alert type="info">{t("auth.access.read-only-banner")}</Alert>
          )}

          {isWriteUser && (
            <SpaceBetween direction="horizontal" size="xl">
              <SpaceBetween size="xs">
                <Box variant="awsui-key-label">{t("scenarios.filters.visibility")}</Box>
                <SpaceBetween direction="horizontal" size="m">
                  <Checkbox
                    checked={showPublic}
                    onChange={({ detail }) => {
                      setShowPublic(detail.checked);
                      try { sessionStorage.setItem(VISIBILITY_PUBLIC_KEY, String(detail.checked)); } catch {}
                    }}
                  >
                    {t("scenarios.visibility.public")}
                  </Checkbox>
                  <Checkbox
                    checked={showPrivate}
                    onChange={({ detail }) => {
                      setShowPrivate(detail.checked);
                      try { sessionStorage.setItem(VISIBILITY_PRIVATE_KEY, String(detail.checked)); } catch {}
                    }}
                  >
                    {t("scenarios.visibility.private")}
                  </Checkbox>
                </SpaceBetween>
              </SpaceBetween>
              <SpaceBetween size="xs">
                <Box variant="awsui-key-label">{t("scenarios.filters.status")}</Box>
                <SpaceBetween direction="horizontal" size="m">
                  <Checkbox
                    checked={showPublished}
                    onChange={({ detail }) => {
                      setShowPublished(detail.checked);
                      try { sessionStorage.setItem(STATUS_PUBLISHED_KEY, String(detail.checked)); } catch {}
                    }}
                  >
                    {t("scenarios.status.published")}
                  </Checkbox>
                  <Checkbox
                    checked={showDraft}
                    onChange={({ detail }) => {
                      setShowDraft(detail.checked);
                      try { sessionStorage.setItem(STATUS_DRAFT_KEY, String(detail.checked)); } catch {}
                    }}
                  >
                    {t("scenarios.status.draft")}
                  </Checkbox>
                </SpaceBetween>
              </SpaceBetween>
            </SpaceBetween>
          )}

          {loading && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spinner size="large" />
              <Box variant="p" margin={{ top: "s" }}>{t("scenarios.view.loading")}</Box>
            </div>
          )}

          {!loading && scenarios.length === 0 && (
            <Box textAlign="center" padding={{ vertical: "xxl" }}>
              <SpaceBetween size="m">
                <Box variant="h2" color="text-body-secondary">{t("scenarios.view.empty_title")}</Box>
                <Box variant="p" color="text-body-secondary">{t("scenarios.view.empty_message")}</Box>
                {isWriteUser && (
                  <Button variant="primary" iconName="add-plus" onClick={() => { setModalMode("create"); setShowModal(true); }}>
                    {t("scenarios.view.add_first")}
                  </Button>
                )}
              </SpaceBetween>
            </Box>
          )}

          {!loading && scenarios.length > 0 && filteredScenarios.length === 0 && (
            <Box textAlign="center" padding={{ vertical: "xxl" }}>
              <SpaceBetween size="m">
                <Box variant="h2" color="text-body-secondary">{t("scenarios.view.no_matches_title")}</Box>
                <Box variant="p" color="text-body-secondary">{t("scenarios.view.no_matches_message")}</Box>
              </SpaceBetween>
            </Box>
          )}

          {!loading && filteredScenarios.length > 0 && (
            <div className="scenarios-grid">
              {filteredScenarios.map((scenario) => (
                <ScenarioTile
                  key={scenario.scenario_id}
                  scenario={scenario}
                  currentUserSub={currentUserSub}
                  onEdit={(s) => { setModalMode("edit"); setSelectedScenario(s); setShowModal(true); }}
                  onDelete={(s) => { setSelectedScenario(s); setShowDeleteModal(true); }}
                  onClick={handleScenarioClick}
                />
              ))}
            </div>
          )}
        </SpaceBetween>
      </Container>

      <ScenarioModal
        mode={modalMode}
        scenario={selectedScenario}
        onSubmit={modalMode === "create" ? handleCreateScenario : handleEditScenario}
        onCancel={() => { setShowModal(false); setSelectedScenario(null); }}
        visible={showModal}
      />

      <DeleteConfirmModal
        scenario={selectedScenario}
        onConfirm={handleDeleteScenario}
        onCancel={() => { setShowDeleteModal(false); setSelectedScenario(null); }}
        visible={showDeleteModal}
        loading={deleteLoading}
      />

      <CallModal
        scenario={selectedScenario}
        callDirection={callDirection}
        onStart={handleCallStart}
        onCancel={() => { setShowCallModal(false); setSelectedScenario(null); }}
        visible={showCallModal}
      />
    </ContentLayout>
  );
}

export default ScenariosView;
